import { useDataDogRUM } from '@/composables/useDataDogRUM';
import { consumeForceLoggedOut } from '@/composables/useCrossTabAuth';
import { usePermissions } from '@/composables/usePermissions';
import { useAuthStore } from '@/stores/auth';
import { loadVueformConditionally } from '@/main-vueform-conditional';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { createRouter, createWebHistory, NavigationFailureType } from 'vue-router';
import { useKnowledgeStore } from '../modules/knowledge/stores/knowledgeStore';
import NotFound from '../views/404NotFound.vue';
import AcsView from '../views/AcsView.vue';
import ForgotPassword from '../views/ForgotPassword.vue';
import Login from '../views/Login.vue';
import ResetPassword from '../views/ResetPassword.vue';

// Get the base URL from environment variables
const BASE_URL = import.meta.env.VITE_APP_BASE_URL || '/';

/**
 * Resolves the document title for a navigation, or returns null when the route's
 * static meta title should NOT be applied (e.g. a query-only change on the same
 * page, which would otherwise clobber a dynamic title set via usePageTitle).
 */
export function resolveRouteTitle(
    to: Pick<RouteLocationNormalized, 'path' | 'meta'>,
    from: Pick<RouteLocationNormalized, 'path'>
): string | null {
    const isQueryOnlyChange = to.path === from.path;
    if (isQueryOnlyChange) {
        return null;
    }

    if (to.meta.title) {
        const title = typeof to.meta.title === 'function' ? to.meta.title(to as RouteLocationNormalized) : to.meta.title;
        return title as string;
    }

    return 'OvationCXM';
}

function requireReviewQueueAccess(
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext
) {
    const { can } = usePermissions();
    if (can.viewReviewQueue()) {
        next();
        return;
    }
    next(to.path.startsWith('/aistudio') ? '/aistudio/home' : '/inbox/home');
}

const { init, trackPageView, addAction, addError } = useDataDogRUM();

const router = createRouter({
    history: createWebHistory(BASE_URL),
    routes: [
        {
            path: '/login',
            name: 'login',
            component: Login,
            meta: {
                title: 'Login - OvationCXM',
            },
            // Redirect to home if already authenticated
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore();

                // Make sure auth store is initialized
                if (!authStore.isInitialized) {
                    await authStore.initialize();
                }

                // After a cross-tab logout, never bounce back to home even if
                // userStatus briefly still reports authenticated (cookie race).
                if (consumeForceLoggedOut()) {
                    if (authStore.isAuthenticated) {
                        authStore.isAuthenticated = false;
                    }
                    next();
                    return;
                }

                // Redirect to home if already authenticated
                if (authStore.isAuthenticated) {
                    console.debug('Already authenticated, redirecting to home');
                    next('/');
                } else {
                    // Allow access to login page
                    next();
                }
            },
        },
        {
          path: '/auth/kb',
          name: 'authorize-kb',
          component: () => import('../views/KnowledgeAuthorize.vue'),
          beforeEnter: async (to, from, next) => {
            console.log('Login authorize route detected, bypassing authentication check')
            next()
            return
          },
          meta: {
            title: 'Login Authorize - OvationCXM'
          }
        },
        {
            path: '/',
            component: () => import('../views/RootLayout.vue'),
            // Protect all routes under root
            beforeEnter: async (to, from, next) => {
                // Allow ACS route to bypass authentication (it will handle its own flow)
                if (to.path === '/auth/saml/acs' || to.name === 'auth/saml/acs') {
                    console.debug('ACS route detected, bypassing authentication check');
                    next();
                    return;
                }

                const authStore = useAuthStore();
                // Make sure auth store is initialized
                if (!authStore.isInitialized) {
                    await authStore.initialize();
                }

                if (authStore.isAuthenticated) {
                    next();
                } else {
                    console.debug('Not authenticated, redirecting to login');
                    // save current route for redirect after login do it in a store
                    // set this to the full url path including query params and hash
                    const url = new URL(window.location.href);
                    const redirectPath = url.pathname + url.search + url.hash;
                    
                    // Only save redirect path if it's not a public route (login, password reset, etc.)
                    // This prevents circular navigation failures
                    if (redirectPath !== '/login' && 
                        !redirectPath.startsWith('/login?') &&
                        redirectPath !== '/passwordreset' && 
                        !redirectPath.startsWith('/passwordreset?')) {
                        authStore.redirectAfterLogin = redirectPath;
                    }
                    next('/login');
                }
            },
            children: [
                {
                    path: '',
                    redirect: '/inbox/home',
                },
                {
                    path: 'inbox/cases/:id/field-services/:section',
                    name: 'inbox-case-fs-popout',
                    component: () => import('../modules/field-services/views/FieldServicesSectionPopout.vue'),
                    meta: {
                        title: 'Field Services - OvationCXM',
                    },
                },
                {
                    path: 'inbox',
                    name: 'inbox',
                    component: () => import('../modules/inbox/views/InboxView.vue'),
                    meta: {
                        title: 'Inbox - OvationCXM',
                    },
                    beforeEnter: (to, from, next) => {
                        // Backward compat: redirect old ?view=home URLs to the dedicated /inbox/home route
                        if (to.query.view === 'home') {
                            next('/inbox/home');
                            return;
                        }
                        if (to.query.object === 'ai-reviews') {
                            requireReviewQueueAccess(to, from, next);
                            return;
                        }
                        // Check if user is navigating directly to /inbox without query parameters
                        if (to.path === '/inbox' && Object.keys(to.query).length === 0) {
                            // Check if there's a saved last inbox URL
                            import('../stores/cases').then(({ useCasesStore }) => {
                                const casesStore = useCasesStore();
                                const lastUrl = casesStore.getAndClearLastInboxUrl();

                                if (lastUrl) {
                                    // Navigate to the saved URL
                                    next(lastUrl);
                                } else {
                                    // Default to inbox home
                                    next('/inbox/home');
                                }
                            });
                            return;
                        }
                        // Allow normal navigation for other cases
                        next();
                    },
                    children: [
                        {
                            path: 'home',
                            name: 'inbox-home',
                            component: () => import('../modules/inbox/components/views-panel/InboxHome.vue'),
                            meta: {
                                title: 'Home - Inbox - OvationCXM',
                            },
                        },
                        {
                            path: 'cases/:id',
                            name: 'inbox-case-detail',
                            component: () => import('../modules/inbox/views/InboxCase.vue'),
                        },
                        {
                            path: 'tasks/:id',
                            name: 'inbox-task-detail',
                            component: () => import('../modules/inbox/views/InboxTask.vue'),
                        },
                        {
                            path: 'task/:id',
                            name: 'inbox-task-detail',
                            component: () => import('../modules/inbox/views/InboxTask.vue'),
                        },
                        {
                            path: 'ai-reviews/:id',
                            name: 'inbox-ai-review-detail',
                            component: () => import('../modules/inbox/views/InboxAiReview.vue'),
                            beforeEnter: requireReviewQueueAccess,
                        },
                    ],
                },
                {
                    path: 'journeys',
                    name: 'journeys',
                    component: () => import('../views/JourneysView.vue'),
                    meta: {
                        title: 'Journeys - OvationCXM',
                    },
                },
                {
                    path: 'customers',
                    name: 'customers',
                    component: () => import('../modules/customer/views/CustomersView.vue'),
                    meta: {
                        title: 'Customers - OvationCXM',
                    },
                    children: [
                        {
                            path: '',
                            name: 'customers-list',
                            component: () => import('../modules/customer/views/CustomersListView.vue'),
                            meta: {
                                title: 'Customers - OvationCXM',
                            },
                        },
                        {
                            path: 'home',
                            name: 'customers-home',
                            component: () => import('../modules/customer/views/CustomersHome.vue'),
                            meta: {
                                title: 'Customers Overview - OvationCXM',
                            },
                        },
                        {
                            path: 'locations',
                            name: 'customers-locations',
                            component: () => import('../modules/customer/views/LocationsListView.vue'),
                            meta: {
                                title: 'Locations - OvationCXM',
                            },
                        },
                        {
                            path: 'contacts',
                            name: 'customers-contacts',
                            component: () => import('../modules/customer/views/ContactsListView.vue'),
                            meta: {
                                title: 'Contacts - OvationCXM',
                            },
                        },
                    ],
                },
                {
                    path: 'customers/:id',
                    name: 'customer-detail',
                    component: () => import('../modules/customer/views/CustomerView.vue'),
                    props: true,
                    meta: {
                        title: 'Customer Detail - OvationCXM',
                    },
                },
                {
                    path: 'customers/locations/:id',
                    name: 'location-detail',
                    component: () => import('../modules/customer/views/LocationView.vue'),
                    props: true,
                    meta: {
                        title: 'Location Detail - OvationCXM',
                    },
                },
                {
                    path: 'customers/contacts/:id',
                    name: 'contact-detail',
                    component: () => import('../modules/customer/views/ContactView.vue'),
                    props: true,
                    meta: {
                        title: 'Contact Detail - OvationCXM',
                    },
                },
                {
                    path: 'knowledge',
                    name: 'knowledge',
                    component: () => import('../modules/knowledge/views/KnowledgeView.vue'),
                    meta: {
                        title: 'Knowledge - OvationCXM',
                    },
                    children: [
                        {
                            path: '',
                            name: 'Knowledge',
                            component: () => import('../modules/knowledge/components/list-view/KnowledgeList.vue'),
                            meta: {
                                title: 'Knowledge - OvationCXM',
                            },
                        },
                        {
                            path: 'articles/:id',
                            name: 'knowledge-article',
                            component: () => import('../modules/knowledge/views/ArticleView.vue'),
                            props: true,
                            meta: {
                                title: 'Article',
                            },
                        },
                    ],
                },
                {
                    path: 'reports',
                    name: 'reports',
                    component: () => import('../modules/Analytics/Views/ReportsView.vue'),
                    meta: {
                        title: 'Reports - OvationCXM',
                    },
                },
                {
                    path: 'reports/:id',
                    name: 'report-detail',
                    component: () => import('../modules/Analytics/Views/ReportDetailsView.vue'),
                    props: true,
                    meta: {
                        title: 'Report Detail - OvationCXM',
                    },
                },
                {
                    path: 'reports/:id/standard',
                    name: 'standard-report-detail',
                    component: () => import('../modules/Analytics/Views/StandardReportDetailsView.vue'),
                    props: true,
                    meta: {
                        title: 'Standard Report - OvationCXM',
                    },
                },
                {
                    path: 'analytics',
                    name: 'analytics',
                    component: () => import('../modules/Analytics/Views/AnalyticsView.vue'),
                    meta: {
                        title: 'Analytics - OvationCXM',
                    },
                },
                {
                    path: 'aistudio',
                    name: 'aistudio',
                    component: () => import('../modules/AIStudio/Views/AIStudioView.vue'),
                    meta: {
                        title: 'AI Studio - OvationCXM',
                    },
                    children: [
                        {
                            path: '',
                            name: 'aistudio-default',
                            redirect: '/aistudio/home',
                        },
                        {
                            path: 'home',
                            name: 'aistudio-home',
                            component: () => import('../modules/AIStudio/components/AIStudioHome.vue'),
                            meta: {
                                title: 'Home - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'agents',
                            name: 'aistudio-agents',
                            component: () => import('../modules/AIStudio/components/agents/AIStudioAgentList.vue'),
                            meta: {
                                title: 'Agents - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'workflows',
                            name: 'aistudio-workflows',
                            component: () => import('../modules/AIStudio/components/workflows/AIStudioWorkflowsList.vue'),
                            meta: {
                                title: 'Workflows - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'workflows/:id',
                            name: 'aistudio-workflow-details',
                            component: () => import('../modules/AIStudio/components/workflows/AIStudioWorkflowDetails.vue'),
                            props: true,
                            meta: {
                                title: 'Workflow Details - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'branding',
                            name: 'aistudio-branding',
                            component: () => import('../modules/AIStudio/components/branding/AIStudioGlobalHome.vue'),
                            meta: {
                                title: 'Global Settings - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'tools',
                            name: 'aistudio-tools',
                            component: () => import('../modules/AIStudio/components/AIStudioToolsList.vue'),
                            meta: {
                                title: 'Tools - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'tools/:toolId',
                            name: 'aistudio-tool-details',
                            component: () => import('../modules/AIStudio/components/tools/AIStudioToolsDetails.vue'),
                            meta: {
                                title: 'Tool Details - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'actions',
                            redirect: '/settings/actions',
                        },
                        {
                            path: 'actions/:id',
                            redirect: (to) => ({
                                path: `/settings/actions/${to.params.id}/details`,
                                query: to.query,
                                hash: to.hash,
                            }),
                        },
                        {
                            path: 'actions/:id/:tab(details|revisions|executions)',
                            redirect: (to) => ({
                                path: `/settings/actions/${to.params.id}/${to.params.tab}`,
                                query: to.query,
                                hash: to.hash,
                            }),
                        },
                        {
                            path: 'mcp-configurations',
                            name: 'aistudio-mcp-configurations',
                            component: () => import('../modules/AIStudio/components/mcp-configurations/McpConfigurationsList.vue'),
                            meta: {
                                title: 'MCP Configurator - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'mcp-configurations/:id',
                            name: 'aistudio-mcp-configuration-details',
                            component: () => import('../modules/AIStudio/components/mcp-configurations/McpConfigurationDetails.vue'),
                            props: true,
                            meta: {
                                title: 'MCP Configuration - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'confirmation-tickets',
                            name: 'aistudio-confirmation-tickets',
                            component: () => import('../modules/AIStudio/components/mcp-configurations/McpConfirmationTicketQueue.vue'),
                            meta: {
                                title: 'Confirmation Tickets - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'models',
                            name: 'aistudio-models',
                            component: () => import('../modules/AIStudio/components/AIStudioModelsHome.vue'),
                            meta: {
                                title: 'Models - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'observability',
                            name: 'aistudio-observability',
                            component: () => import('../modules/AIStudio/components/AIStudioPerformance.vue'),
                            meta: {
                                title: 'Observability - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'observability/traces/:name',
                            name: 'aistudio-trace-list',
                            component: () => import('../modules/AIStudio/components/performance/AIStudioTraceList.vue'),
                            props: true,
                            meta: {
                                title: 'Trace List - Observability - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'observability/traces/:name/:id',
                            name: 'aistudio-trace-detail',
                            component: () => import('../modules/AIStudio/components/performance/AIStudioTraceDetail.vue'),
                            props: true,
                            meta: {
                                title: 'Trace Details - Observability - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'agents/:id',
                            name: 'aistudio-agent-config',
                            component: () => import('../modules/AIStudio/components/agents/AIAgentConfigRouter.vue'),
                            props: true,
                            meta: {
                                title: 'Agent Configuration - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'evaluations',
                            name: 'aistudio-evaluations',
                            component: () => import('../modules/AIStudio/components/evaluations/AIStudioEvaluations.vue'),
                            meta: {
                                title: 'Evaluations - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'evaluations/datasets/:datasetName',
                            name: 'aistudio-dataset-detail',
                            component: () => import('../modules/AIStudio/components/evaluations/AIStudioDatasetDetail.vue'),
                            props: true,
                            meta: {
                                title: 'Dataset Details - Evaluations - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'customer-agent-home',
                            name: 'aistudio-customer-agent-home',
                            component: () => import('../modules/AIStudio/components/customer-agents/CustomerAgentHome.vue'),
                            meta: {
                                title: 'ATCL Home - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'customer-agents',
                            name: 'aistudio-customer-agents',
                            component: () => import('../modules/AIStudio/components/customer-agents/CustomerAgentsList.vue'),
                            meta: {
                                title: 'Client Agents - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'customer-agent-activity',
                            name: 'aistudio-customer-agent-activity',
                            component: () => import('../modules/AIStudio/components/customer-agents/CustomerAgentActivityList.vue'),
                            meta: {
                                title: 'Agent Activity - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'customer-agents/:id',
                            name: 'aistudio-client-agent-details',
                            component: () => import('../modules/AIStudio/components/customer-agents/CustomerAgentDetails.vue'),
                            props: true,
                            meta: {
                                title: 'Client Agent Details - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'evaluations/datasets/:datasetName/experiments/:experimentId',
                            name: 'aistudio-experiment-detail',
                            component: () => import('../modules/AIStudio/components/evaluations/AIStudioExperimentDetail.vue'),
                            props: true,
                            meta: {
                                title: 'Experiment Results - Evaluations - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'reviews',
                            name: 'aistudio-reviews',
                            component: () => import('../modules/AIStudio/components/reviews/AIStudioReviewQueue.vue'),
                            beforeEnter: requireReviewQueueAccess,
                            meta: {
                                title: 'Review Queue - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'reviews/:reviewId',
                            name: 'aistudio-review-detail',
                            component: () => import('../modules/AIStudio/components/reviews/AIStudioReviewDetail.vue'),
                            props: true,
                            beforeEnter: requireReviewQueueAccess,
                            meta: {
                                title: 'Review Detail - AI Studio - OvationCXM',
                            },
                        },
                    ],
                },
                {
                    path: 'field-services',
                    name: 'field-services',
                    component: () => import('../modules/field-services/views/FieldServicesView.vue'),
                    meta: {
                        title: 'Field Services - OvationCXM',
                    },
                    children: [
                        {
                            path: '',
                            name: 'field-services-default',
                            redirect: '/field-services/home',
                        },
                        {
                            path: 'home',
                            name: 'field-services-home',
                            component: () => import('../modules/field-services/components/FieldServicesHome.vue'),
                            meta: {
                                title: 'Home - Field Services - OvationCXM',
                            },
                        },
                        {
                            path: 'technicians',
                            name: 'field-services-technicians',
                            component: () => import('../modules/field-services/components/FieldServicesTechnicianList.vue'),
                            meta: {
                                title: 'Technicians - Field Services - OvationCXM',
                            },
                        },
                        {
                            path: 'technicians/:id',
                            name: 'field-services-technician-detail',
                            component: () => import('../modules/field-services/components/FieldServicesTechnicianDetails.vue'),
                            props: true,
                            meta: {
                                title: 'Technician Details - Field Services - OvationCXM',
                            },
                        },
                        {
                            path: 'map',
                            name: 'field-services-map',
                            component: () => import('../modules/field-services/components/FieldServicesMap.vue'),
                            meta: {
                                title: 'Technician Map - Field Services - OvationCXM',
                            },
                        },
                        {
                            path: 'invoicing',
                            name: 'field-services-invoicing',
                            component: () => import('../modules/field-services/views/FieldServicesInvoicingView.vue'),
                            meta: {
                                title: 'Invoicing - Field Services - OvationCXM',
                            },
                        },
                        {
                            path: 'quality-assurance',
                            name: 'field-services-quality-assurance',
                            component: () => import('../modules/field-services/components/QualityAssuranceContent.vue'),
                            meta: {
                                title: 'Quality Assurance - Field Services - OvationCXM',
                            },
                        },
                        {
                            path: 'sow-template-builder',
                            name: 'field-services-sow-template-builder',
                            component: () => import('../modules/field-services/views/SowBuilderView.vue'),
                            meta: {
                                title: 'SOW Template Builder - Field Services - OvationCXM',
                            },
                        },
                        {
                            path: 'estimate-template-builder',
                            name: 'field-services-estimate-template-builder',
                            component: () => import('../modules/field-services/views/EstimateTemplateBuilderView.vue'),
                            meta: {
                                title: 'Estimate Template Builder - Field Services - OvationCXM',
                            },
                        },
                        {
                            path: 'settings',
                            name: 'field-services-settings',
                            component: () => import('../modules/field-services/components/FieldServicesSettingsPanel.vue'),
                            meta: {
                                title: 'Settings - Field Services - OvationCXM',
                            },
                        },
                    ],
                },
                {
                    path: 'settings',
                    name: 'settings',
                    redirect: '/settings/home',
                    meta: {
                        title: 'Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/fields/tags/:tagType/:id',
                    name: 'settings-tag-detail',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Tag Details - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/fields/tags/:tagType',
                    name: 'settings-tags-list',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Tags - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/fields/:subsection/:id',
                    name: 'settings-field-detail',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Field Details - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/fields',
                    name: 'settings-fields',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    meta: {
                        title: 'Fields - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/templates/:type/:id',
                    name: 'settings-template-detail',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Template Details - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/actions/:id/:tab(details|revisions|executions)',
                    name: 'settings-action-details',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Action Details - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/actions/:id',
                    redirect: (to) => ({
                        name: 'settings-action-details',
                        params: { id: to.params.id, tab: 'details' },
                        query: to.query,
                        hash: to.hash,
                    }),
                },
                {
                    path: 'settings/layouts/tables',
                    name: 'settings-layouts-tables',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    meta: {
                        title: 'Table Layouts - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/layouts/cards/:id',
                    name: 'settings-card-detail',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Card Detail - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/:section/:id?',
                    name: 'settings-section',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: (route: RouteLocationNormalized) => {
                            const section = route.params.section;
                            const id = route.params.id;

                            if (section === 'layouts' && id) {
                                return 'Layout Detail - OvationCXM';
                            }

                            if (section) {
                                // Format section for display (capitalize and replace dashes with spaces)
                                const formattedSection = section
                                    .toString()
                                    .split('-')
                                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');
                                return `${formattedSection} Settings - OvationCXM`;
                            }
                            return 'Settings - OvationCXM';
                        },
                    },
                    children: [
                        {
                            path: 'submissions',
                            name: 'settings-form-submissions',
                            component: () => import('../modules/settings/components/forms/SettingsFormsSubmissionsList.vue'),
                            props: true,
                            meta: {
                                title: 'Form Submissions - OvationCXM',
                            },
                        },
                        {
                            path: 'submissions/:submissionId',
                            name: 'settings-form-submission-view',
                            component: () => import('../modules/settings/components/forms/SettingsFormsSubmissionView.vue'),
                            props: true,
                            meta: {
                                title: 'Form Submission View - OvationCXM',
                            },
                        },
                        {
                            path: 'webhooks/:webhookId',
                            name: 'settings-apis-webhook-details',
                            component: () => import('../modules/settings/views/SettingsView.vue'),
                            props: true,
                            meta: {
                                title: 'Webhook Details - API Settings - OvationCXM',
                            },
                        },
                        {
                            path: 'saml/:samlId',
                            name: 'settings-security-saml-details',
                            component: () => import('../modules/settings/views/SettingsView.vue'),
                            props: true,
                            meta: {
                                title: 'SAML Integration Details - Security Settings - OvationCXM',
                            },
                        },
                        {
                            path: 'details/:salesforceId',
                            name: 'settings-salesforce-details',
                            component: () => import('../modules/settings/views/SettingsView.vue'),
                            props: true,
                            meta: {
                                title: 'Salesforce Integration Details - OvationCXM',
                            },
                        },
                    ],
                },
                {
                    path: 'profile',
                    name: 'profile',
                    component: () => import('../views/UserProfile.vue'),
                    meta: {
                        title: 'User Profile - OvationCXM',
                    },
                },
            ],
        },
        {
            path: '/auth/saml/acs',
            name: 'auth/saml/acs',
            component: AcsView,
            meta: {
                title: 'ACS Login - OvationCXM',
            },
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: NotFound,
            meta: {
                title: '404 - Page Not Found',
            },
        },
        {
            path: '/passwordreset',
            name: 'passwordreset',
            component: ForgotPassword,
            meta: {
                title: 'Forgot Password - OvationCXM',
            },
            // Redirect to home if already authenticated
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore();

                // Make sure auth store is initialized
                if (!authStore.isInitialized) {
                    await authStore.initialize();
                }

                // Redirect to home if already authenticated
                if (authStore.isAuthenticated) {
                    console.debug('Already authenticated, redirecting to login');
                    next('/login');
                } else {
                    // Allow access to login page
                    next();
                }
            },
        },
        {
            path: '/passwordchange/:token/:email',
            name: 'passwordchange',
            component: ResetPassword,
            meta: {
                title: 'Change Password - OvationCXM',
            },
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore();

                // Wait for the auth store to initialize if needed
                if (!authStore.isInitialized) {
                    await authStore.initialize();
                }

                // Redirect to login if already authenticated
                if (authStore.isAuthenticated) {
                    console.debug('Already authenticated, redirecting to login');
                    next('/login');
                } else {
                    next();
                }
            },
        },
    ],
});

// Load Vueform lazily the first time the user navigates to settings/forms
router.beforeEach(async (to, from, next) => {
    if (to.path.includes('/settings/forms')) {
        await loadVueformConditionally()
    }
    next()
})

router.beforeEach((to, from, next) => {
    // If we're navigating away from the knowledge base section
    if (from.path.startsWith('/knowledge') && !to.path.startsWith('/knowledge')) {
        const knowledgeStore = useKnowledgeStore();
        knowledgeStore.clearCurrentList();
        knowledgeStore.clearCurrentArticle();
    }
    // If we're navigating away from a specific article
    else if (from.name === 'knowledge-article' && to.name !== 'knowledge-article') {
        const knowledgeStore = useKnowledgeStore();
        knowledgeStore.clearCurrentArticle();
    }

    // Update document title based on route meta, unless this is a query-only
    // change on the same page (see resolveRouteTitle for why).
    const resolvedTitle = resolveRouteTitle(to, from);
    if (resolvedTitle !== null) {
        document.title = resolvedTitle;
    }

    next();
});

// Global router error handler
router.onError((error, to, from) => {
    console.error('🚨 Router navigation error:', error);
    
    // Check if this is a dynamic import failure
    const isDynamicImportError = 
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Loading CSS chunk');
    
    if (isDynamicImportError) {
        console.warn('🔄 Dynamic import failed, this should be handled by useRouterNavigation composable');
    }
    
    // Log the error with comprehensive metadata for debugging
    addError(error, {
        // Navigation context
        from: from?.fullPath || 'unknown',
        from_name: from?.name?.toString() || 'unknown',
        to: to?.fullPath || 'unknown',
        to_name: to?.name?.toString() || 'unknown',
        
        // Error details
        error_message: error?.message || 'Unknown error',
        error_name: error?.name || 'Error',
        error_stack: error?.stack || 'No stack trace available',
        error_cause: error?.cause ? String(error.cause) : undefined,
        
        // Error classification
        isDynamicImportError,
        error_source: 'router',
        
        // Timestamp
        timestamp: new Date().toISOString(),
    });
});

router.afterEach((to, from, failure) => {
    if (!failure) {
        // Track page view with route name and path
        trackPageView(to.path, to.name?.toString());

        // Log navigation action
        addAction('navigation', {
            from: from.fullPath,
            to: to.fullPath,
            routeName: to.name?.toString(),
        });
    }
    // Note: Navigation failures (duplicated, cancelled, aborted) are not logged to Datadog
    // as they are expected behaviors, not actual errors. Real errors like missing components
    // or dynamic import failures are handled by router.onError() above.
});

export default router;
